import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import * as dialog from "@zag-js/dialog";
import { Portal, normalizeProps, useMachine } from "@zag-js/react";
import {
  Hammer, Star, MapPin, Navigation, Car, Bed, Bath, Maximize,
  ArrowRight, ChevronRight, Search, Phone,
  CircleCheck, Home as House,
} from "lucide-react";
import { properties } from "../data/malickeCase";
import { PropertyDetailModal } from "../components/malickecase/PropertyDetailModal";

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 999,
        width: 44, height: 44, borderRadius: "50%",
        background: "#0E3A3A", color: "#fff", border: "none",
        cursor: "pointer", fontSize: 18, lineHeight: 1,
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      }}
    >↑</button>
  );
}

/* -- nav ------------------------------------------------------- */
function MCNav({ active }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur border-b border-brand/10">
      <div className="mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8 max-w-7xl">
        <Link to="/" aria-label="Malicke Case home" className="group flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand text-coral shadow-soft transition-transform group-hover:scale-105">
            <Hammer className="h-5 w-5" aria-hidden />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-brand">
            Malicke <span className="text-coral">Case</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 rounded-full border border-brand/10 bg-white/60 p-1 backdrop-blur">
          {[["Home", "/home"], ["Services", "/services"], ["Listings", "/listings"]].map(([label, to]) => (
            <Link key={to} to={to}
              className={`relative rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
                active === label.toLowerCase() ? "bg-brand text-white shadow-soft" : "text-ink-soft hover:text-brand"
              }`}
            >{label}</Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+18768279793" className="text-sm font-medium text-ink-soft hover:text-brand transition-colors">+1 (876) 827-9793</a>
          <a href="#contact" className="rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white shadow-coral-glow transition-transform hover:scale-105">
            Enquire now
          </a>
        </div>
        <button className="md:hidden grid h-10 w-10 place-items-center rounded-lg text-brand hover:bg-brand/5" onClick={() => setOpen(v => !v)} aria-label="Open menu">
          <span className="flex flex-col gap-1.5">
            <span className="block h-0.5 w-5 bg-brand rounded" />
            <span className="block h-0.5 w-5 bg-brand rounded" />
            <span className="block h-0.5 w-3 bg-brand rounded" />
          </span>
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-brand/10 px-4 py-4 flex flex-col gap-3">
          {[["Home", "/home"], ["Services", "/services"], ["Listings", "/listings"]].map(([label, to]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="text-sm font-medium text-ink hover:text-coral">{label}</Link>
          ))}
          <a href="#contact" onClick={() => setOpen(false)} className="mt-2 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white text-center shadow-coral-glow">
            Enquire now
          </a>
        </div>
      )}
    </header>
  );
}

function PreviewNotice({ open, listingName, onContinue, onClose }) {
  const service = useMachine(dialog.machine, {
    id: "listings-preview-notice",
    open,
    onOpenChange: ({ open: nextOpen }) => {
      if (!nextOpen) onClose();
    },
  });
  const api = dialog.connect(service, normalizeProps);

  if (!api.open) return null;

  return (
    <Portal>
      <div
        {...api.getBackdropProps()}
        style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(5, 5, 6, 0.84)", backdropFilter: "blur(6px)" }}
      />
      <div
        {...api.getPositionerProps()}
        style={{ position: "fixed", inset: 0, zIndex: 201, display: "grid", placeItems: "center", padding: 20 }}
      >
        <div
          {...api.getContentProps()}
          data-testid="listings-preview-notice"
          style={{ width: "100%", maxWidth: 448, border: "1px solid #2A2A35", borderRadius: 18, background: "#111114", color: "#F5F5F7", padding: "28px 28px 24px", boxShadow: "0 24px 80px rgba(0,0,0,0.45)" }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 18 }}>
            <div>
              <p style={{ margin: 0, fontFamily: "monospace", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "#FF4D8D" }}>Listings preview</p>
              <h2 {...api.getTitleProps()} style={{ margin: "12px 0 0", fontSize: 30, lineHeight: 1.08, letterSpacing: "-0.03em" }}>This listing is a sample.</h2>
            </div>
            <button {...api.getCloseTriggerProps()} type="button" aria-label="Close preview notice" style={{ border: 0, background: "transparent", color: "#A1A1AA", fontSize: 22, lineHeight: 1, cursor: "pointer", padding: 2 }}>×</button>
          </div>
          <p {...api.getDescriptionProps()} style={{ margin: "16px 0 0", color: "#D4D4D8", fontSize: 14, lineHeight: 1.65 }}>
            {listingName ? `${listingName} is part of the design preview. ` : "These listings are part of the design preview. "}Do not use this page for a live enquiry yet.
          </p>
          <button
            type="button"
            onClick={onContinue}
            style={{ width: "100%", marginTop: 22, border: 0, borderRadius: 12, background: "#FF1F6D", color: "#050506", padding: "13px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            View the sample
          </button>
        </div>
      </div>
    </Portal>
  );
}

/* -- data ------------------------------------------------------ */
const AMENITIES_ALL = [...new Set(properties.flatMap(p => p.amenities))].sort();

const PROCESS_STEPS = [
  { n: "01", title: "Browse & shortlist", body: "Filter by type, location, or amenities. Every card shows beds, baths, size, and drive time from the airport." },
  { n: "02", title: "Open the map", body: "Tap any listing for the full photo gallery, GPS pin, and animated driving route with step-by-step directions to the gate." },
  { n: "03", title: "Book a viewing", body: "Call or message us. We arrange access with the owner or our crew and confirm a time that works for you." },
  { n: "04", title: "Contact the owner", body: "We put you in direct contact with whoever holds the keys. No agent middleman, no hidden fees." },
];

const FEATURES = [
  { title: "Exact GPS location", body: "Every listing includes a precise GPS pin. No vague \"near Ochi\"  -  you get the address and coordinates." },
  { title: "Student friendly", body: "We have listings near UWI Mona and UTECH, with short-let and semester-lease options available." },
  { title: "Interactive map & route", body: "Open any listing and get an animated driving route with turn-by-turn directions from any starting point on the island." },
  { title: "St. Ann & Kingston", body: "We cover two parishes  -  north coast or the capital, we have something." },
  { title: "No hidden paperwork", body: "Owner-listed properties are shown as-is. Crew-renovated properties come with a full condition report." },
  { title: "Future listings first", body: "Sign up and we'll alert you before a new property goes live to the public." },
];

/* -- page ------------------------------------------------------ */
export default function ListingsPage() {
  useEffect(() => { document.documentElement.classList.remove("dark"); }, []);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [amenityFilters, setAmenityFilters] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAcknowledged, setPreviewAcknowledged] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  function openListing(id) {
    if (previewAcknowledged) {
      setSelectedId(id);
      return;
    }
    setPendingId(id);
    setPreviewOpen(true);
  }

  function continueToListing() {
    setPreviewAcknowledged(true);
    setPreviewOpen(false);
    if (pendingId) setSelectedId(pendingId);
    setPendingId(null);
  }

  const toggleAmenity = (a) => setAmenityFilters(prev =>
    prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]
  );

  const visible = useMemo(() => properties.filter(p => {
    if (filter === "rent" && p.status !== "For Rent") return false;
    if (filter === "lease" && p.status !== "For Lease") return false;
    if (filter === "sale" && p.status !== "For Sale") return false;
    if (filter === "student" && !p.student) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.location.toLowerCase().includes(search.toLowerCase())) return false;
    if (amenityFilters.length > 0 && !amenityFilters.every(a => p.amenities.includes(a))) return false;
    return true;
  }), [filter, search, amenityFilters]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <MCNav active="listings" />
      <PreviewNotice
        open={previewOpen}
        listingName={pendingId ? properties.find(p => p.id === pendingId)?.name : ""}
        onContinue={continueToListing}
        onClose={() => { setPreviewOpen(false); setPendingId(null); }}
      />
      <PropertyDetailModal propertyId={selectedId} onClose={() => setSelectedId(null)} />
      <main className="flex-1">

        {/* -- Hero ---------------------------------------- */}
        <section className="relative overflow-hidden bg-brand-ink text-white">
          <div className="absolute inset-0">
            <img src="https://sfile.chatglm.cn/images-ppt/99869d95750e.jpg"
              alt="Property in Jamaica"
              className="h-full w-full object-cover opacity-50 animate-ken-burns" />
            <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink/80 to-brand/30" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-32">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-3 py-1 text-xs font-medium">
                <Star className="h-3 w-3 text-coral fill-coral" /> St. Ann and Kingston listings
              </div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mt-6 leading-[1.05]">
                Find your next home in St. Ann or Kingston.
              </h1>
              <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed">
                Every property below is available for rent, lease, or sale. Some we renovated ourselves, others are listed free by owners across the island. Tap any listing for the full gallery, interactive map, and driving directions.
              </p>
              <p className="mt-4 inline-flex rounded-full border border-coral/50 bg-coral/15 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                Coming soon · sample listings
              </p>
              <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/70">
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-coral" />Exact GPS location for every listing</span>
                <span className="flex items-center gap-1.5"><Navigation className="h-4 w-4 text-coral" />Driving route with animated arrows</span>
                <span className="flex items-center gap-1.5"><Car className="h-4 w-4 text-coral" />Step-by-step directions to the gate</span>
              </div>
            </div>
          </div>
        </section>

        {/* -- Stats bar ----------------------------------- */}
        <section className="bg-coral text-white border-y border-white/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[["9+","Active listings"],["Free","For property owners"],["2","Parishes covered"],["Students","UWI & UTECH welcome"]].map(([val, label]) => (
                <div key={label}>
                  <p className="font-display text-3xl md:text-4xl font-bold text-white">{val}</p>
                  <p className="text-xs md:text-sm text-white/80 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* -- Listings ------------------------------------ */}
        <section className="bg-sand">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-widest uppercase text-coral">Available listings</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-ink mt-3 leading-tight">
                Properties for rent, lease, and sale.
              </h2>
              <p className="mt-4 text-ink-soft text-lg leading-relaxed">
                Some we renovated ourselves, others are listed free by owners. Tap any listing for the full gallery, an interactive map, and step-by-step directions.
              </p>
            </div>

            {/* Filter row */}
            <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
              <div className="flex flex-wrap gap-2">
                {[["all","All listings"],["rent","For Rent"],["lease","For Lease"],["sale","For Sale"],["student","Student"]].map(([val, label]) => (
                  <button key={val} onClick={() => setFilter(val)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                      filter === val ? "bg-brand text-white" : "bg-white border border-brand/10 text-ink hover:bg-brand/5"
                    }`}
                  >{label}</button>
                ))}
              </div>
              <div className="relative md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
                <input
                  placeholder="Search by name, town, or parish..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full rounded-full bg-white border border-brand/10 pl-10 pr-4 py-2.5 text-sm placeholder:text-ink-soft/60 focus:outline-none focus:border-coral"
                />
              </div>
            </div>

            {/* Amenity chips */}
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-soft">Amenities</span>
                {amenityFilters.length > 0 && (
                  <button onClick={() => setAmenityFilters([])} className="text-[11px] text-coral font-semibold hover:underline">
                    Clear ({amenityFilters.length})
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(showAllAmenities ? AMENITIES_ALL : AMENITIES_ALL.slice(0, 12)).map(a => (
                  <button key={a} onClick={() => toggleAmenity(a)}
                    className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors border ${
                      amenityFilters.includes(a)
                        ? "bg-brand border-brand text-white"
                        : "border-brand/20 text-ink-soft bg-white hover:border-brand hover:text-brand"
                    }`}
                  >{a}</button>
                ))}
              </div>
              {AMENITIES_ALL.length > 12 && (
                <button
                  onClick={() => setShowAllAmenities(s => !s)}
                  className="mt-2.5 text-[11px] font-medium text-brand hover:underline"
                >
                  {showAllAmenities ? "Show less" : `+ ${AMENITIES_ALL.length - 12} more amenities`}
                </button>
              )}
            </div>

            {/* Property cards */}
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.length === 0 && (
                <div className="col-span-3 py-20 text-center text-ink-soft">
                  No listings match your filters.{" "}
                  <button onClick={() => { setFilter("all"); setSearch(""); setAmenityFilters([]); setShowAllAmenities(false); }}
                    className="text-coral font-semibold hover:underline">Reset filters</button>
                </div>
              )}
              {visible.map(p => (
                <article key={p.id} onClick={() => openListing(p.id)}
                  className="group cursor-pointer rounded-3xl bg-white border border-brand/10 overflow-hidden shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={p.heroImage} alt={p.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 to-transparent" />
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                      <span className="rounded-full bg-coral px-3 py-1 text-xs font-semibold text-white shadow-soft">{p.status}</span>
                      {p.student && <span className="rounded-full bg-white/95 backdrop-blur px-3 py-1 text-xs font-semibold text-coral shadow-soft">Student</span>}
                      <span className="rounded-full bg-brand/90 px-3 py-1 text-xs font-semibold text-white shadow-soft">Sample listing</span>
                    </div>
                    <span className={`absolute top-3 right-3 rounded-full px-3 py-1 text-xs font-semibold shadow-soft ${p.crewRenovated ? "bg-white/95 backdrop-blur text-brand" : "bg-brand text-white"}`}>
                      {p.crewRenovated ? "Crew-renovated" : "Owner-listed"}
                    </span>
                    <div className="absolute bottom-3 left-3 right-3 text-white">
                      <p className="text-xs text-white/80 flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</p>
                      <h3 className="font-display text-xl font-bold mt-0.5 leading-tight">{p.name}</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-2xl font-bold text-ink">
                      {p.price}{p.pricePeriod && <span className="text-xs text-ink-soft font-normal"> {p.pricePeriod}</span>}
                    </p>
                    <p className="text-sm text-ink-soft mt-1 line-clamp-2">{p.tagline}</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-ink">
                      <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5 text-coral" />{p.beds} beds</span>
                      <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5 text-coral" />{p.baths} baths</span>
                      <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5 text-coral" />{p.sqft.toLocaleString()} ft²</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-brand/10 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1 text-ink-soft">
                        <Car className="h-3.5 w-3.5 text-coral" />{p.estDriveTime} from airport
                      </span>
                      <span className="inline-flex items-center gap-1 font-semibold text-brand group-hover:text-coral transition-colors">
                        View &amp; directions <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* -- List your property -------------------------- */}
        <section className="bg-brand text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-xs font-semibold tracking-widest uppercase text-coral">Property owners</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
                  List your property for free.
                </h2>
                <p className="mt-4 text-white/70 text-lg leading-relaxed">
                  We list your property at no cost. You keep full control  -  set your own price, approve every enquiry, and we handle the map and the reach.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a href="tel:+18768279793" className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-semibold shadow-coral-glow hover:scale-[1.03] transition-transform">
                    <Phone className="h-4 w-4" /> Call to list
                  </a>
                  <a href="#contact" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/5 px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors">
                    Send details
                  </a>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  ["Exactly zero fees","No listing fees, no commission, no hidden charges."],
                  ["Student friendly","We actively market to UWI and UTECH students looking for housing."],
                  ["Interactive map included","Every listing gets a GPS pin and driving directions at no charge."],
                  ["Renew anytime","Update price, photos, or status anytime with a quick message."],
                ].map(([title, body]) => (
                  <div key={title} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                    <CircleCheck className="h-5 w-5 text-coral mb-3" />
                    <p className="font-semibold text-sm">{title}</p>
                    <p className="text-xs text-white/60 mt-1 leading-relaxed">{body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* -- 4-step process ------------------------------ */}
        <section className="bg-sand">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold tracking-widest uppercase text-coral">How it works</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-ink mt-3 leading-tight">
                From browse to keys in four steps.
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {PROCESS_STEPS.map((s, i) => (
                <div key={s.n} className="relative rounded-2xl bg-white border border-brand/10 p-6 shadow-soft">
                  <span className="font-display text-5xl font-bold text-coral/30">{s.n}</span>
                  <h3 className="font-display text-xl font-bold text-ink mt-2">{s.title}</h3>
                  <p className="text-sm text-ink-soft mt-2 leading-relaxed">{s.body}</p>
                  {i < 3 && <ChevronRight className="hidden lg:block absolute -right-3 top-1/2 h-5 w-5 text-coral/40" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* -- Feature highlights -------------------------- */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <div className="max-w-2xl mb-12">
              <p className="text-xs font-semibold tracking-widest uppercase text-coral">Why list with us</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mt-3">New listings, real reach.</h2>
              <p className="mt-4 text-ink-soft text-lg">We put your listing on the map, find it a route, and make it easy for the right tenant or buyer to find you.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map(f => (
                <div key={f.title} className="rounded-2xl bg-sand border border-brand/10 p-6">
                  <House className="h-6 w-6 text-coral mb-3" />
                  <h3 className="font-display font-bold text-ink">{f.title}</h3>
                  <p className="text-sm text-ink-soft mt-2 leading-relaxed">{f.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* -- CTA footer ---------------------------------- */}
        <section id="contact" className="bg-coral text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 text-center">
            <p className="text-xs font-semibold tracking-widest uppercase text-white/70">Ready to move</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 leading-tight">Let's get you to the gate.</h2>
            <p className="mt-4 text-lg text-white/80 max-w-xl mx-auto">
              Call us about any listing and we'll arrange a viewing, answer questions, or put you in direct contact with the owner.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:+18768279793" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-coral px-8 py-3.5 text-sm font-semibold hover:bg-sand transition-colors">
                <Phone className="h-4 w-4" /> +1 (876) 827-9793
              </a>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-3.5 text-sm font-semibold hover:bg-white/20 transition-colors">
                See our services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

      </main>
      <BackToTop />
    </div>
  );
}
